import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { OpticalJobListItem } from '@optiflow/types';
import { useCurrency } from '@/hooks/useCurrency';
import { useLocale } from '@/hooks/useLocale';
import { formatDate } from '@/utils/format';
import { JobStatusBadge } from './JobStatusBadge';
import { PrescriptionOrigin } from './PrescriptionOrigin';

interface JobListProps {
  jobs: readonly OpticalJobListItem[];
  /** Leave the customer out where the whole list is one customer's. */
  showCustomer?: boolean;
  /** Name the branch where the list spans more than one. */
  showBranch?: boolean;
}

/**
 * Jobs as rows that stay actionable at every width.
 *
 * Each row's View link names its job for assistive technology and stretches over the whole row, so a phone can open any job with one tap and a keyboard reaches one link per row. Where a prescription came from is shown — a clinical number or "External" — never its values; money only when the reader may see it.
 */
export function JobList({ jobs, showCustomer = true, showBranch = false }: JobListProps) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { format } = useCurrency();

  return (
    <ul className="divide-line divide-y">
      {jobs.map((job) => {
        const frame = job.frame
          ? [job.frame.productName, job.frame.variantName].filter(Boolean).join(' — ') ||
            job.frame.sku
          : null;
        return (
          <li
            key={job.id}
            className="hover:bg-canvas relative flex flex-col gap-2 px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1.3fr)_minmax(0,1fr)_auto] sm:items-center sm:gap-4"
          >
            <div className="min-w-0 space-y-1">
              <p className="flex flex-wrap items-center gap-2">
                <span className="text-ink font-mono text-sm font-semibold" dir="ltr">
                  {job.reference}
                </span>
                <JobStatusBadge status={job.status} />
              </p>
              <p className="text-muted text-xs">
                <span className="font-mono" dir="ltr">
                  {job.orderReference}
                </span>
                {' · '}
                <time dateTime={job.orderedAt}>{formatDate(job.orderedAt, locale)}</time>
                {showBranch ? ` · ${job.branch.name}` : ''}
              </p>
            </div>

            <div className="min-w-0 space-y-1 text-sm">
              {showCustomer ? (
                <p className="text-ink truncate font-medium" dir="auto">
                  {job.customer?.displayName ?? t('opticalOrders.tracking.noCustomer')}
                </p>
              ) : null}
              <p className="text-muted truncate text-xs" dir="auto">
                {frame ?? t('opticalOrders.tracking.frameNotRecorded')}
              </p>
            </div>

            <div className="min-w-0 space-y-1 text-xs">
              <p className="text-ink">
                <PrescriptionOrigin prescription={job.prescription} />
              </p>
              <p className="text-muted">
                {job.lab
                  ? `${job.lab.name}${job.lab.isActive ? '' : ` (${t('opticalOrders.labs.inactive')})`}`
                  : t('opticalOrders.tracking.noLab')}
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 sm:justify-end">
              {job.balance ? (
                <span className="text-xs">
                  {job.balance.outstandingMinor === 0 ? (
                    <span className="text-emerald-700">{t('opticalOrders.tracking.paid')}</span>
                  ) : (
                    <span className="font-medium text-amber-800">
                      {t('opticalOrders.tracking.owing', {
                        amount: format(job.balance.outstandingMinor),
                      })}
                      {job.balance.coveredByPlan ? ` · ${t('opticalOrders.tracking.onPlan')}` : ''}
                    </span>
                  )}
                </span>
              ) : (
                <span />
              )}
              <Link
                to={`/sales/optical-jobs/${job.id}`}
                aria-label={t('opticalOrders.tracking.viewJob', { reference: job.reference })}
                className="text-brand-700 focus-visible:ring-brand-200 inline-flex items-center gap-1 rounded text-sm font-medium after:absolute after:inset-0 after:content-[''] focus-visible:ring-2 focus-visible:outline-none"
              >
                {t('opticalOrders.tracking.view')}
                <ChevronRight className="size-4 rtl:rotate-180" aria-hidden />
              </Link>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
